"use client";

import Image from "@/components/Image/Image.component";
import { Typography } from "@/components/Typography/Typography.component";
import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import * as Progress from "@radix-ui/react-progress";
import { gsap } from "gsap";
import { useEffect, useRef, useState } from "react";

export interface LoadingProps {
  /** Custom className */
  className?: string;
  /** Custom icon source */
  iconSrc?: string;
  /** Icon size */
  iconSize?: { width: number; height: number };
  /** Whether to auto complete */
  autoComplete?: boolean;
  /** Completion callback */
  onComplete?: () => void;
  /** Child components */
  children?: React.ReactNode;
}

export function Loading({
  className,
  iconSrc = "/static/icons/pageLoading.gif",
  iconSize = { width: 80, height: 80 },
  autoComplete = true,
  onComplete,
  children,
}: LoadingProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  gsap.registerPlugin(useGSAP);

  useGSAP(
    () => {
      const transitionElement = nodeRef.current;

      if (transitionElement && progress === 100) {
        const tl = gsap.timeline();

        tl.startTime(1)
          .from(transitionElement, {
            yPercent: 0,
          })
          .to(transitionElement, {
            yPercent: 100,
            duration: 0.5,
            onComplete: () => {
              onComplete?.();
            },
          });
      }
    },
    { scope: nodeRef, dependencies: [progress] },
  );

  useEffect(() => {
    if (autoComplete) {
      let current_progress = 0;

      const interval = setInterval(function () {
        current_progress += 3;
        const progress = Math.round(
          ((Math.atan(current_progress) / (Math.PI / 2)) * 100 * 1000) / 1000,
        );
        setTimeout(() => {
          setProgress(100);
          clearInterval(interval);
        }, 800);
        setProgress(Math.min(progress, 100));
      }, 100);

      return () => clearInterval(interval);
    }
  }, [autoComplete]);

  return (
    <>
      <div
        ref={nodeRef}
        className={cn(
          "bg-core-black fixed top-0 left-0 z-[100] flex h-screen w-screen items-center justify-center",
          className,
        )}
      >
        <div className="grid items-center justify-center gap-y-[14rem]">
          <Image
            width={iconSize.width}
            height={iconSize.height}
            src={iconSrc}
            alt="loading"
            className="m-auto"
            priority
            data-nimg="1"
          />
          <div className="flex items-center justify-center">
            <Progress.Root
              className="bg-core-black relative h-2 w-28 overflow-hidden"
              style={{
                // Fix overflow clipping in Safari
                // https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
                transform: "translateZ(0)",
              }}
              value={progress}
              aria-label="loadingprogress"
            >
              <Progress.Indicator
                className="ease-[cubic-bezier(0.65, 0, 0.35, 1)] bg-core-white h-full w-full transition-transform duration-700"
                style={{ transform: `translateX(-${100 - progress}%)` }}
              />
            </Progress.Root>
            <div className="w-8 text-center">
              <Typography.H7 className="text-core-white !text-xs">
                {progress}%
              </Typography.H7>
            </div>
          </div>
        </div>
      </div>
      {children}
    </>
  );
}
